import type {
  AccessModuleKey,
  AccessPermissionKey,
} from "@/types/accessControl";

// Register new module keys, permission keys, and route coverage here
// whenever a new feature needs role-aware visibility or actions.
export const ACCESS_MODULE_KEYS = {
  OVERVIEW: "overview",
  INBOX: "inbox",
  ONBOARDING: "onboarding",
  ONBOARDING_MICROAPP: "onboarding_microapp",
  AUDIT_TRAIL: "audit_trail",
  USERS: "users",
  CLIENTS: "clients",
  RENEWALS: "renewals",
  PACKAGE_SERVICES: "package_services",
  TASKS: "tasks",
  CHAT: "chat",
  LEADS: "leads",
  REPORTS: "reports",
  SOCIAL_MEDIA: "social_media",
} as const satisfies Record<string, AccessModuleKey>;

export const ACCESS_PERMISSION_KEYS = {
  OVERVIEW_VIEW: "overview.view",
  INBOX_VIEW: "inbox.view",
  ONBOARDING_VIEW: "onboarding.view",
  ONBOARDING_MICROAPP_VIEW: "onboarding_microapp.view",
  ONBOARDING_SEND_WELCOME_EMAIL: "onboarding.send_welcome_email",
  ONBOARDING_UPLOAD_DIGITAL_MARKETING_PLAN:
    "onboarding.upload_digital_marketing_plan",
  ONBOARDING_COMPLETE_DIGITAL_MARKETING_PLAN:
    "onboarding.complete_digital_marketing_plan",
  ONBOARDING_UPLOAD_SERVICE_AGREEMENT: "onboarding.upload_service_agreement",
  ONBOARDING_CREATE_SOCIAL_MEDIA_ACCESS:
    "onboarding.create_social_media_access",
  ONBOARDING_EDIT_SOCIAL_MEDIA_ACCESS: "onboarding.edit_social_media_access",
  ONBOARDING_DELETE_SOCIAL_MEDIA_ACCESS:
    "onboarding.delete_social_media_access",
  ONBOARDING_SAVE_SERVICE_DETAILS: "onboarding.save_service_details",
  AUDIT_TRAIL_VIEW: "audit_trail.view",
  USERS_VIEW: "users.view",
  USERS_CREATE: "users.create",
  USERS_EDIT: "users.edit",
  USERS_DELETE: "users.delete",
  USERS_RESET_PASSWORD: "users.reset_password",
  USERS_MANAGE_SIDENAV: "users.manage_sidenav",
  USERS_MANAGE_PERMISSIONS: "users.manage_permissions",
  CLIENTS_VIEW: "clients.view",
  CLIENTS_CREATE: "clients.create",
  CLIENTS_EDIT: "clients.edit",
  CLIENTS_MANAGE_ACCESS: "clients.manage_access",
  CLIENTS_ARCHIVE: "clients.archive",
  CLIENTS_RESTORE: "clients.restore",
  CLIENTS_DELETE: "clients.delete",
  CLIENTS_SYNC_DMP_CLIENTS: "clients.sync_dmp_clients",
  RENEWALS_VIEW: "renewals.view",
  PACKAGE_SERVICES_VIEW: "package_services.view",
  PACKAGE_SERVICES_CREATE: "package_services.create",
  PACKAGE_SERVICES_EDIT: "package_services.edit",
  PACKAGE_SERVICES_ASSIGN: "package_services.assign",
  PACKAGE_SERVICES_ARCHIVE: "package_services.archive",
  PACKAGE_SERVICES_DELETE: "package_services.delete",
  PACKAGE_SERVICES_VIEW_SERVICES: "package_services.view_services",
  PACKAGE_SERVICES_CREATE_SERVICES: "package_services.create_services",
  PACKAGE_SERVICES_EDIT_SERVICES: "package_services.edit_services",
  PACKAGE_SERVICES_DELETE_SERVICES: "package_services.delete_services",
  PACKAGE_SERVICES_VIEW_DEFAULT_TASKS: "package_services.view_default_tasks",
  PACKAGE_SERVICES_CREATE_DEFAULT_TASKS:
    "package_services.create_default_tasks",
  PACKAGE_SERVICES_EDIT_DEFAULT_TASKS: "package_services.edit_default_tasks",
  PACKAGE_SERVICES_DELETE_DEFAULT_TASKS:
    "package_services.delete_default_tasks",
  TASKS_CREATE: "tasks.create",
  TASKS_EDIT: "tasks.edit",
  TASKS_ASSIGN: "tasks.assign",
  TASKS_COMPLETE: "tasks.complete",
  TASKS_DELETE: "tasks.delete",
  TASKS_VIEW_TICKETS: "tasks.view_tickets",
  TASKS_CREATE_TICKET: "tasks.create_ticket",
  TASKS_EDIT_TICKET: "tasks.edit_ticket",
  TASKS_DELETE_TICKET: "tasks.delete_ticket",
  CHAT_VIEW: "chat.view",
  LEADS_VIEW: "leads.view",
  LEADS_CREATE: "leads.create",
  LEADS_EDIT: "leads.edit",
  LEADS_DELETE: "leads.delete",
  LEADS_MASTERLIST: "leads.masterlist",
  REPORTS_VIEW: "reports.view",
  REPORTS_GENERATE: "reports.generate",
  REPORTS_EXPORT_PDF: "reports.export_pdf",
  REPORTS_AI_EDIT: "reports.ai_edit",
  TASKS_VIEW: "tasks.view",
} as const satisfies Record<string, AccessPermissionKey>;

export interface AccessModuleRouteRule {
  moduleKey: AccessModuleKey;
  prefixes: string[];
}

export const ACCESS_MODULE_ROUTE_RULES: AccessModuleRouteRule[] = [
  { moduleKey: ACCESS_MODULE_KEYS.OVERVIEW, prefixes: ["/overview"] },
  { moduleKey: ACCESS_MODULE_KEYS.INBOX, prefixes: ["/inbox", "/emails"] },
  {
    moduleKey: ACCESS_MODULE_KEYS.ONBOARDING_MICROAPP,
    prefixes: ["/onboarding/microapp"],
  },
  {
    moduleKey: ACCESS_MODULE_KEYS.ONBOARDING,
    prefixes: ["/onboarding", "/client/onboarding", "/apps/onboarding"],
  },
  { moduleKey: ACCESS_MODULE_KEYS.AUDIT_TRAIL, prefixes: ["/activity"] },
  { moduleKey: ACCESS_MODULE_KEYS.USERS, prefixes: ["/users"] },
  {
    moduleKey: ACCESS_MODULE_KEYS.CLIENTS,
    prefixes: ["/clients", "/users/clients", "/email-templates"],
  },
  {
    moduleKey: ACCESS_MODULE_KEYS.RENEWALS,
    prefixes: ["/renewals"],
  },
  {
    moduleKey: ACCESS_MODULE_KEYS.PACKAGE_SERVICES,
    prefixes: ["/admin/package-services"],
  },
  {
    moduleKey: ACCESS_MODULE_KEYS.TASKS,
    prefixes: ["/apps/task-dashboard", "/apps/tasks", "/operations/task"],
  },
  {
    moduleKey: ACCESS_MODULE_KEYS.CHAT,
    prefixes: ["/communication/chat"],
  },
  { moduleKey: ACCESS_MODULE_KEYS.LEADS, prefixes: ["/leads"] },
  { moduleKey: ACCESS_MODULE_KEYS.REPORTS, prefixes: ["/reports"] },
  {
    moduleKey: ACCESS_MODULE_KEYS.SOCIAL_MEDIA,
    prefixes: ["/social-media", "/apps/social-media"],
  },
];

export interface AccessPermissionRouteRule {
  permissionKey: AccessPermissionKey;
  prefixes: string[];
}

export const ACCESS_PERMISSION_ROUTE_RULES: AccessPermissionRouteRule[] = [
  {
    permissionKey: ACCESS_PERMISSION_KEYS.USERS_MANAGE_PERMISSIONS,
    prefixes: ["/settings/system-apps"],
  },
];
